import Decimal from 'decimal.js';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as React from 'react';
import { usePrevious } from 'react-use';

import { FullScreenModalProps } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { AISuggestionsMatcherArrayField } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import {
  EditEntitySplitScreenModalContext,
  SubmitButtonType,
} from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import {
  CombinedSupportedSubformFieldTypes,
  EditEntitySection,
  isBDGSection,
  isDPSection,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { useEditEntitySplitScreenSubformConfigs } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntitySplitScreenSubformConfigs';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
// eslint-disable-next-line luminary-rules/no-page-imports
import { ManageDispositionPageSections } from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionPage.types';

import { EntityType } from '../../types/EntityType';
import { getSectionOptions } from '../EditEntitySplitScreen.utils';
import { useEditEntityTrowser } from '../hooks/useEditEntityTrowser';

interface EditEntitySplitScreenModalProviderProps {
  initialSection: EditEntitySection;
  entityType: EntityType;
  entityValueOverride?: Decimal;
  footerRef: React.RefObject<HTMLDivElement | null>;
  headerRef: React.RefObject<HTMLDivElement | null>;
  duplicatesRefs: Partial<
    Record<
      AISuggestionsMatcherArrayField,
      React.RefObject<HTMLDivElement | null>
    >
  >;
  onClose: FullScreenModalProps['onClose'];
  onAfterSave?: () => void;
}

function useContextValue({
  initialSection,
  entityType,
  entityValueOverride,
  footerRef,
  headerRef,
  duplicatesRefs,
  onClose,
  onAfterSave,
}: EditEntitySplitScreenModalProviderProps): EditEntitySplitScreenModalContext {
  const { navigate } = useNavigateToRoute();
  const { householdId, primaryClients } = useHouseholdDetailsContext();

  const { setSection } = useEditEntityTrowser();

  const { subformConfigsWithInitialData, hasError } =
    useEditEntitySplitScreenSubformConfigs();

  const { resetAcknowledgedSuggestions } =
    useSuggestionAcknowledgementContext();

  const { handleRequestCloseOuterModal } = useDPFormRecipientTrackerContext();

  const [currentSection, setCurrentSection] =
    useState<EditEntitySplitScreenModalContext['currentSection']>(
      initialSection
    );

  const [clickedSubmitButton, setClickedSubmitButton] =
    useState<SubmitButtonType>(null);

  const prevCurrentSection = usePrevious(currentSection);

  useEffect(() => {
    if (prevCurrentSection !== currentSection) {
      // Don't reset acknowledged suggestions when switching between DP death
      // order sections. The mutation already saved them to the backend, but the
      // suggestions refetch hasn't updated previouslyAcknowledgedSuggestions yet,
      // so resetting here would lose the just-accepted suggestions.
      const isSwitchingBetweenDPSections =
        isDPSection(prevCurrentSection) && isDPSection(currentSection);
      if (!isSwitchingBetweenDPSections) {
        resetAcknowledgedSuggestions();
      }
      // Reset the clicked button when switching sections
      setClickedSubmitButton(null);
      setSection(currentSection);
    }
  }, [
    currentSection,
    prevCurrentSection,
    resetAcknowledgedSuggestions,
    setSection,
  ]);

  const sectionOptions = useMemo(() => {
    return getSectionOptions(entityType, primaryClients);
  }, [entityType, primaryClients]);

  const { currentSectionSubformConfig, initialSubformValues } = useMemo(() => {
    const sectionConfigsWithData =
      subformConfigsWithInitialData[currentSection];
    if (!sectionConfigsWithData) {
      return {
        currentSectionSubformConfig: undefined,
        initialSubformValues: undefined,
      };
    }

    // Combine the initial data for all sections/subforms.
    const initialSubformValues = Object.values(
      subformConfigsWithInitialData
    ).reduce((prev, curr) => {
      const namespace = curr.subformConfig.type;
      // @ts-ignore -- Currently, we only support a subset of all subform
      // namespace types in the CombinedSupportedSubformFieldTypes object.
      prev[namespace] = curr.initialData;
      return prev;
    }, {} as CombinedSupportedSubformFieldTypes);

    return {
      currentSectionSubformConfig: sectionConfigsWithData.subformConfig,
      initialSubformValues,
    };
  }, [subformConfigsWithInitialData, currentSection]);

  const handleCloseModal = useCallback(
    (skipChecks?: boolean) => {
      if (!householdId) return; // Type-safe check for householdId

      if (skipChecks) {
        // If skipChecks is true, close the modal without checking anything
        onClose?.();
        return;
      }

      handleRequestCloseOuterModal(onClose, () => {
        // Navigate to the Manage Dispositions page to the TE tab
        navigate(
          ROUTE_KEYS.HOUSEHOLD_ESTATE_MANAGE_DISPOSITIONS,
          {
            householdId,
          },
          {
            section: ManageDispositionPageSections.TESTAMENTARY_ENTITIES,
          }
        );
      });
    },
    [handleRequestCloseOuterModal, householdId, navigate, onClose]
  );

  const isDPSectionValue = useMemo(
    () => isDPSection(currentSection),
    [currentSection]
  );

  const isBDGSectionValue = useMemo(
    () => isBDGSection(currentSection),
    [currentSection]
  );

  return useMemo(
    () => ({
      entityType,
      entityValueOverride,
      footerRef,
      headerRef,
      duplicatesRefs,
      sectionOptions,
      currentSectionSubformConfig,
      initialSubformValues,
      hasError,
      currentSection,
      setCurrentSection,
      clickedSubmitButton,
      setClickedSubmitButton,
      handleCloseModal,
      isDPSection: isDPSectionValue,
      isBDGSection: isBDGSectionValue,
      onAfterSave,
    }),
    [
      entityType,
      entityValueOverride,
      footerRef,
      headerRef,
      duplicatesRefs,
      sectionOptions,
      currentSectionSubformConfig,
      initialSubformValues,
      hasError,
      currentSection,
      clickedSubmitButton,
      handleCloseModal,
      isDPSectionValue,
      isBDGSectionValue,
      onAfterSave,
    ]
  );
}

export const EditEntitySplitScreenModalProvider = ({
  children,
  ...rest
}: PropsWithChildren<EditEntitySplitScreenModalProviderProps>) => {
  const value = useContextValue(rest);

  return (
    <EditEntitySplitScreenModalContext.Provider value={value}>
      {children}
    </EditEntitySplitScreenModalContext.Provider>
  );
};
